import {
  DATA_TABLE_STATE_PREFIX,
  DATAGRID_ID_PREFIX,
} from '~components/DataTable/dataTableStorageKeys';

const SAVED_STATE_VERSION = 1;

interface DataTableSavedState {
  columnOrder: string[];
  // Only the columns the user showed or hid, so a page's defaults still apply to
  // every column the user never touched.
  columnVisibility: Record<string, boolean>;
  columnWidths: Record<string, number>;
}

export const EMPTY_SAVED_STATE: DataTableSavedState = {
  columnOrder: [],
  columnVisibility: {},
  columnWidths: {},
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toStringArray = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

const toColumnVisibility = (value: unknown) =>
  isRecord(value)
    ? Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
        ),
      )
    : {};

const toColumnWidths = (entries: Array<[string, unknown]>) =>
  Object.fromEntries(
    entries.filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0,
    ),
  );

function readStoredJson(key: string) {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null') as unknown;
  } catch {
    return null;
  }
}

function parseSavedState(value: unknown): DataTableSavedState | null {
  if (!isRecord(value) || value.version !== SAVED_STATE_VERSION) {
    return null;
  }

  return {
    columnOrder: toStringArray(value.columnOrder),
    columnVisibility: toColumnVisibility(value.columnVisibility),
    columnWidths: isRecord(value.columnWidths)
      ? toColumnWidths(Object.entries(value.columnWidths))
      : {},
  };
}

// MUI saves dimensions for every column whose definition declares a width or flex,
// dragged or not. Dragging or double-click fitting a column sets its `flex` to 0, and
// no column definition declares `flex: 0`, so that marks the widths the user chose.
function toDraggedDataGridWidths(dimensions: unknown) {
  if (!isRecord(dimensions)) {
    return {};
  }

  const draggedWidths = Object.entries(dimensions).flatMap(([field, dimension]) =>
    isRecord(dimension) && dimension.flex === 0
      ? [[field, dimension.width] as [string, unknown]]
      : [],
  );

  return toColumnWidths(draggedWidths);
}

// Reads the `columns` slice of MUI DataGrid's `exportState()`.
function parseDataGridState(value: unknown): DataTableSavedState | null {
  if (!isRecord(value) || !isRecord(value.columns)) {
    return null;
  }

  const { columnVisibilityModel, dimensions, orderedFields } = value.columns;

  return {
    columnOrder: toStringArray(orderedFields),
    columnVisibility: toColumnVisibility(columnVisibilityModel),
    columnWidths: toDraggedDataGridWidths(dimensions),
  };
}

export function readSavedState(tableId: string) {
  return (
    parseSavedState(readStoredJson(`${DATA_TABLE_STATE_PREFIX}${tableId}`)) ??
    parseDataGridState(readStoredJson(`${DATAGRID_ID_PREFIX}${tableId}`)) ??
    EMPTY_SAVED_STATE
  );
}

export function writeSavedState(tableId: string, state: DataTableSavedState) {
  try {
    localStorage.setItem(
      `${DATA_TABLE_STATE_PREFIX}${tableId}`,
      JSON.stringify({ ...state, version: SAVED_STATE_VERSION }),
    );
  } catch (error) {
    console.warn(`Failed to save the layout of table ${tableId}:`, error);
  }
}

// Saved orders go stale as pages add and remove columns. A column the saved order
// doesn't know yet is placed after its nearest preceding sibling in the default order.
export function reconcileColumnOrder(savedOrder: string[], defaultOrder: string[]) {
  const knownFields = new Set(defaultOrder);
  const order = [...new Set(savedOrder)].filter((field) => knownFields.has(field));

  defaultOrder.forEach((field, defaultIndex) => {
    if (order.includes(field)) {
      return;
    }

    const precedingField = defaultOrder
      .slice(0, defaultIndex)
      .reverse()
      .find((candidate) => order.includes(candidate));
    const insertAt = precedingField ? order.indexOf(precedingField) + 1 : 0;
    order.splice(insertAt, 0, field);
  });

  return order;
}
